import React, { EventHandler, KeyboardEvent, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { ValidationRow } from '../validationRow/ValidationRow'
import { ValidationPendingRow } from '../validationPendingRow/ValidationPendingRow'

import { ErrorView, SearchAndDateFilter, Table, TabPanel } from '@percent/admin-dashboard/common/components'
import { TableColumn, TableSort } from '@percent/admin-dashboard/common/components/table/Table.types'
import { useBadgeTotal } from '@percent/admin-dashboard/context/badgeTotal/useBadgeTotal/useBadgeTotal'
import { useGetQuery } from '@percent/admin-dashboard/common/hooks/useGetQuery/useGetQuery'
import {
  POSTHOG_FEATURE_FLAGS,
  usePostHogFeatureFlag
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import { ValidationsTableProps } from './ValidationsTable.types'

const sortMapTableNamesToProperties = { organization: 'name', requestDate: 'createdAt', timeLeft: 'id' }

export function ValidationsTable({
  totalResults,
  validations,
  isLoading,
  previousPage,
  nextPage,
  queryParams,
  setQueryParams,
  errorMessage
}: ValidationsTableProps) {
  const { t } = useTranslation()
  const { totalNumber, setTotalNumber } = useBadgeTotal()
  const query = useGetQuery()
  const getQueryStatus = query.get('status')

  useEffect(() => {
    if (getQueryStatus === 'pending') {
      setTotalNumber({
        ...totalNumber,
        totalPendingValidations: totalResults ?? 0
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalResults])

  // Also when the URL already filters by it - a link, or the flag still loading - so the selected tab matches the list.
  const showAwaitingDocuments =
    usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_VR_DOCUMENT_REQUEST) || queryParams.status === 'awaiting_documents'

  // One tab per status filter, then "All", which has none.
  const statusTabs = useMemo(
    () => [
      { status: 'pending', label: t('table.pending') },
      ...(showAwaitingDocuments ? [{ status: 'awaiting_documents', label: t('table.awaitingDocuments') }] : []),
      { status: 'accepted', label: t('table.approved') },
      { status: 'rejected', label: t('table.rejected') }
    ],
    [showAwaitingDocuments, t]
  )
  const tabs = [...statusTabs, { status: undefined, label: t('table.all') }]
  const selectedTab = statusTabs.findIndex(({ status }) => status === queryParams.status)
  const tabValue = selectedTab === -1 ? statusTabs.length : selectedTab

  const columns = useMemo(() => {
    const unsortableColumn = (id: string): TableColumn => ({ id, isSortable: false })

    const commonColumns: Array<TableColumn> = [
      unsortableColumn('country'),
      { id: 'organization', isSortable: true },
      unsortableColumn('partner'),
      { id: 'requestDate', isSortable: true, props: { width: '100px' } }
    ]

    if (queryParams.status !== 'pending') {
      commonColumns.splice(2, 0, ...['registry', 'registryId'].map(unsortableColumn))
    }

    if (queryParams.status === 'pending') {
      commonColumns.push({ id: 'timeLeft', isSortable: true })
    }

    return commonColumns
  }, [queryParams.status])

  const mapTableHeaderToProperty = (tableHeaderName: string) =>
    Object.keys(sortMapTableNamesToProperties).includes(tableHeaderName)
      ? sortMapTableNamesToProperties[tableHeaderName as keyof typeof sortMapTableNamesToProperties]
      : null

  const mapPropertyToTableHeader = (propertyName: string | null | undefined) => {
    return Object.keys(sortMapTableNamesToProperties).find(
      key => sortMapTableNamesToProperties[key as keyof typeof sortMapTableNamesToProperties] === propertyName
    )
  }

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) =>
    setQueryParams({
      direction: queryParams.direction === 'ASC' ? 'DESC' : 'ASC',
      sort: mapTableHeaderToProperty(property)
    })

  const handleChange = (_: React.ChangeEvent<{}>, newValue: number) => {
    const status = statusTabs[newValue]?.status

    if (!status) {
      setQueryParams({ status: undefined, sort: undefined, direction: undefined })
    } else if (status === 'pending') {
      setQueryParams({ status, sort: 'id', direction: 'ASC' })
    } else {
      setQueryParams({ status, sort: undefined, direction: undefined })
    }
  }

  const handleKeyPress: EventHandler<KeyboardEvent<HTMLInputElement>> = useCallback(
    event => {
      if (event.key === 'Enter') {
        setQueryParams({
          query: (event.target as HTMLInputElement).value
        })
      }
    },
    [setQueryParams]
  )

  const filters = useMemo(
    () => (
      <SearchAndDateFilter
        handleKeyPress={handleKeyPress}
        searchPhrase={queryParams.query ?? ''}
        maxLength={60}
        placeholder={
          queryParams.status === 'pending' ? t('table.adminVSearchPlaceholder') : t('table.adminVSearchPlaceholderTwo')
        }
      />
    ),
    [handleKeyPress, queryParams.query, queryParams.status, t]
  )

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <Table
      isLoading={isLoading}
      totalResults={totalResults}
      data={validations}
      previousPage={previousPage}
      nextPage={nextPage}
      value={tabValue}
      handleChange={handleChange}
      handleRequestSort={handleRequestSort}
      orderBy={mapPropertyToTableHeader(queryParams?.sort)}
      order={(queryParams.direction?.toLowerCase() as TableSort) ?? 'desc'}
      columns={columns}
      tabs={tabs.map(({ label }) => label)}
      emptyTableText={t(!queryParams.query ? 'table.emptyValidations' : 'table.emptySearchResults')}
      emptyTableImageType="validations"
      filtersContent={filters}
    >
      {tabs.map(({ status }, index) => (
        <TabPanel key={status ?? 'all'} value={tabValue} index={index}>
          {validations?.map(row =>
            status === 'pending' ? (
              <ValidationPendingRow row={row} key={row.id} />
            ) : (
              <ValidationRow row={row} key={row.id} />
            )
          )}
        </TabPanel>
      ))}
    </Table>
  )
}
